import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  getParticipatingTendersMaster,
  getParticipatingTenderOptions,
  updateParticipatingTender,
} from '../services/api';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatDate';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import TechnicalBidDetail from './TechnicalBidDetail';
import AssignTechnicalBidModal from './AssignTechnicalBidModal';
import AssignFinancialBidModal from './AssignFinancialBidModal';
import FinancialBidDetail from './FinancialBidDetail';
import TenderViewModal from './TenderViewModal';
import './TenderList.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function MasterTenderList({ user, canManage = false, onGoToDiscovery }) {
  const { toast } = useToast();
  const statusModalRef = useRef(null);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [options, setOptions] = useState({ sources: [], statuses: [] });
  const [viewingTender, setViewingTender] = useState(null);
  const [statusUpdateTender, setStatusUpdateTender] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [assignTBTender, setAssignTBTender] = useState(null);
  const [technicalBidDetailId, setTechnicalBidDetailId] = useState(null);
  const [assignFBTender, setAssignFBTender] = useState(null);
  const [financialBidDetailId, setFinancialBidDetailId] = useState(null);

  const closeStatusModal = () => {
    setStatusUpdateTender(null);
    setNewStatus('');
  };
  const isStatusModalOpen = !!statusUpdateTender;
  useModalScrollLock(isStatusModalOpen);
  useFocusTrap(statusModalRef, isStatusModalOpen, closeStatusModal);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const monthOptions = MONTH_NAMES.map((name, i) => ({ value: String(i + 1), label: name }));

  useEffect(() => {
    getParticipatingTenderOptions().then((r) => {
      if (r.success) setOptions(r.options || { sources: [], statuses: [] });
    }).catch(() => {});
  }, []);

  const fetchTenders = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getParticipatingTendersMaster({
        search: searchTerm || undefined,
        year: yearFilter ? Number(yearFilter) : undefined,
        month: monthFilter ? Number(monthFilter) : undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (res.success) setTenders(res.tenders || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, yearFilter, monthFilter, sortBy, sortDir]);

  useEffect(() => {
    fetchTenders();
  }, [fetchTenders]);

  const groupedByYearMonth = useMemo(() => {
    const map = {};
    tenders.forEach((t) => {
      const raw = t.updated_at || t.go_ahead_date || t.submission_deadline;
      const d = raw ? new Date(raw) : new Date();
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const key = `${y}-${String(m).padStart(2, '0')}`;
      if (!map[key]) map[key] = { year: y, month: m, tenders: [] };
      map[key].tenders.push(t);
    });
    return Object.entries(map)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([k, v]) => ({ key: k, ...v }));
  }, [tenders]);

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };
  const getStatusClass = (s) => ({ participating: 'status-participating', submitted: 'status-submitted', won: 'status-won', lost: 'status-lost', dropped: 'status-dropped', canceled: 'status-canceled' }[s] || 'status-default');
  const getSourceClass = (s) => ({ gem: 'source-gem', cppp: 'source-cppp', state: 'source-state', private: 'source-private' }[s] || 'source-default');
  const emdStatusClass = (s) => ({ created: 'status-participating', submitted: 'status-submitted', locked: 'status-won', released: 'status-default', forfeited: 'status-lost', closed: 'status-default' }[s] || 'status-default');
  const emdStatusDisplay = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '–';
  const technicalBidStatusClass = (s) => ({ assigned: 'status-participating', in_progress: 'status-participating', submitted: 'status-submitted', approved: 'status-won', rejected: 'status-lost' }[s] || 'status-default');
  const financialBidStatusClass = (s) => ({ assigned: 'status-participating', in_progress: 'status-participating', submitted: 'status-submitted', approved: 'status-won', rejected: 'status-lost' }[s] || 'status-default');
  const isDeadlinePassed = (d) => d && new Date(d) < new Date();
  const isDeadlineApproaching = (d) => {
    if (!d) return false;
    const x = new Date(d) - new Date();
    return x > 0 && x <= 7 * 24 * 60 * 60 * 1000;
  };
  const canAssignTechnicalBid = (t) => canManage && !t.technical_bid_id && ['participating', 'submitted'].includes(t.status);
  const canAssignFinancialBid = (t) => canManage && !t.financial_bid_id && ['participating', 'submitted'].includes(t.status);

  const handleStatusUpdate = async () => {
    if (!newStatus || !statusUpdateTender) return;
    try {
      setActionLoading(true);
      const res = await updateParticipatingTender(statusUpdateTender.id, { status: newStatus });
      if (res.success) {
        closeStatusModal();
        fetchTenders();
        if (viewingTender?.id === statusUpdateTender.id) setViewingTender(res.tender);
        toast.success('Tender status updated');
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setYearFilter('');
    setMonthFilter('');
  };
  const hasActiveFilters = searchTerm || yearFilter || monthFilter;

  const renderTenderRow = (tender, index) => (
    <React.Fragment key={tender.id}>
      <tr
        onClick={() => setExpandedId(expandedId === tender.id ? null : tender.id)}
        className={`tender-row tender-row-clickable ${isDeadlinePassed(tender.submission_deadline) ? 'deadline-passed' : ''} ${expandedId === tender.id ? 'expanded' : ''}`}
      >
        <td className="expand-cell">
          <svg className={`expand-icon ${expandedId === tender.id ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
        </td>
        <td><button type="button" className="ref-code ref-code-link" onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}>{tender.tender_ref}</button></td>
        <td><span className="tender-title-inline">{tender.title || '–'}</span></td>
        <td><span className={`source-badge ${getSourceClass(tender.source)}`}>{tender.source_display}</span></td>
        <td>{tender.buyer_name || '-'}</td>
        <td><span className={`deadline ${isDeadlineApproaching(tender.submission_deadline) ? 'approaching' : ''} ${isDeadlinePassed(tender.submission_deadline) ? 'passed' : ''}`}>{formatDate(tender.submission_deadline)}</span></td>
        <td>{formatCurrency(tender.tender_value)}</td>
        <td><span className="product-count">{(tender.products && tender.products.length) || 0} items</span></td>
        <td><span className={`status-badge ${getStatusClass(tender.status)}`}>{tender.status_display}</span></td>
        <td>{tender.emd_status ? <span className={`status-badge ${emdStatusClass(tender.emd_status)}`}>{emdStatusDisplay(tender.emd_status)}</span> : <span style={{ color: '#9ca3af' }}>–</span>}</td>
        <td>
          {tender.technical_bid_id ? (
            <button type="button" className="link-btn" style={{ padding: 0, fontSize: '13px' }} onClick={(e) => { e.stopPropagation(); setTechnicalBidDetailId(tender.technical_bid_id); }}>
              <span className={`status-badge ${technicalBidStatusClass(tender.technical_bid_status)}`}>{tender.technical_bid_status_display || tender.technical_bid_status}</span> View
            </button>
          ) : canAssignTechnicalBid(tender) ? (
            <button type="button" className="link-btn" style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); setAssignTBTender(tender); }}>Assign</button>
          ) : <span style={{ color: '#9ca3af' }}>–</span>}
        </td>
        <td>
          {tender.financial_bid_id ? (
            <button type="button" className="link-btn" style={{ padding: 0, fontSize: '13px' }} onClick={(e) => { e.stopPropagation(); setFinancialBidDetailId(tender.financial_bid_id); }}>
              <span className={`status-badge ${financialBidStatusClass(tender.financial_bid_status)}`}>{tender.financial_bid_status_display || tender.financial_bid_status}</span> View
            </button>
          ) : canAssignFinancialBid(tender) ? (
            <button type="button" className="link-btn" style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); setAssignFBTender(tender); }}>Assign</button>
          ) : <span style={{ color: '#9ca3af' }}>–</span>}
        </td>
        {canManage && (
          <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
            <button className="edit-btn" onClick={() => { setStatusUpdateTender(tender); setNewStatus(tender.status); }} title="Update Status">Edit</button>
            <button className="edit-btn" onClick={() => setViewingTender(tender)} title="View">View</button>
          </td>
        )}
      </tr>
      {expandedId === tender.id && (
        <tr className="tender-detail-row">
          <td colSpan={canManage ? 12 : 11}>
            <div className="tender-expanded-details">
              <div className="tender-details-grid">
                <div className="detail-item"><label>Buyer</label><span>{tender.buyer_name || '-'}</span></div>
                <div className="detail-item"><label>Deadline</label><span>{formatDate(tender.submission_deadline)}</span></div>
                <div className="detail-item"><label>Value</label><span>{formatCurrency(tender.tender_value)}</span></div>
                <div className="detail-item"><label>Status</label><span className={`status-badge ${getStatusClass(tender.status)}`}>{tender.status_display}</span></div>
              </div>
              <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}>View full details →</button>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  return (
    <div className="tender-list-container">
      <div className="tender-list-header">
        <div className="header-title">
          <h2>Master Tender List</h2>
          <p>All tenders, year-wise and month-wise. Sortable by time.</p>
        </div>
      </div>

      <div className="filters-section">
        <form onSubmit={(e) => { e.preventDefault(); fetchTenders(); }} className="search-box">
          <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <button type="submit">Search</button>
        </form>
        <div className="filter-dropdowns">
          <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
            <option value="">All years</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="">All months</option>
            {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="updated_at">Sort by Updated</option>
            <option value="go_ahead_date">Sort by Go-ahead date</option>
            <option value="submission_deadline">Sort by Deadline</option>
          </select>
          <select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
          {hasActiveFilters && <button type="button" className="btn-secondary" onClick={() => { clearFilters(); fetchTenders(); }}>Clear filters</button>}
        </div>
      </div>

      {error && (
        <div className="error-message">{error}<button onClick={() => setError(null)}>×</button></div>
      )}

      {loading ? (
        <div className="tenders-table-wrapper">
          <table className="tenders-table table-skeleton">
            <thead><tr><th /><th>Reference</th><th>Title</th><th>Source</th><th>Buyer</th><th>Deadline</th><th>Value</th><th>Products</th><th>Status</th><th>EMD</th><th>Technical</th><th>Financial</th>{canManage && <th>Actions</th>}</tr></thead>
            <tbody>{[1,2,3,4,5].map((i) => <tr key={i}><td colSpan={canManage ? 13 : 12}><span className="skeleton-box" style={{ width: '100%' }} /></td></tr>)}</tbody>
          </table>
        </div>
      ) : groupedByYearMonth.length === 0 ? (
        <div className="empty-state">
          <h3>No tenders found</h3>
          <p>Adjust filters or clear year/month to see all tenders.</p>
        </div>
      ) : (
        <>
          {groupedByYearMonth.map((group) => (
            <div key={group.key} className="master-list-group" style={{ marginBottom: 24 }}>
              <h3 className="master-list-group-title">{group.year} – {MONTH_NAMES[group.month - 1]} ({group.tenders.length})</h3>
              <div className="tenders-table-wrapper">
                <table className="tenders-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }} />
                      <th>Reference</th>
                      <th>Title</th>
                      <th>Source</th>
                      <th>Buyer</th>
                      <th>Deadline</th>
                      <th>Value</th>
                      <th>Products</th>
                      <th>Status</th>
                      <th>EMD</th>
                      <th>Technical</th>
                      <th>Financial</th>
                      {canManage && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {group.tenders.map((tender) => renderTenderRow(tender))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <div className="list-footer"><span>Total: {tenders.length} tenders</span></div>
        </>
      )}

      {viewingTender && (
        <TenderViewModal
          tender={viewingTender}
          canManage={canManage}
          onClose={() => setViewingTender(null)}
          onOpenTechnicalBid={(id) => { setViewingTender(null); setTechnicalBidDetailId(id); }}
          onOpenFinancialBid={(id) => { setViewingTender(null); setFinancialBidDetailId(id); }}
          onUpdateStatus={(t) => { setViewingTender(null); setStatusUpdateTender(t); setNewStatus(t.status); }}
        />
      )}
      {assignTBTender && <AssignTechnicalBidModal tender={assignTBTender} onClose={() => setAssignTBTender(null)} onSuccess={(tb) => { fetchTenders(); if (tb?.id) setTechnicalBidDetailId(tb.id); }} />}
      {technicalBidDetailId && <TechnicalBidDetail technicalBidId={technicalBidDetailId} user={user} canManage={canManage} onClose={() => { setTechnicalBidDetailId(null); fetchTenders(); }} />}
      {assignFBTender && <AssignFinancialBidModal tender={assignFBTender} onClose={() => setAssignFBTender(null)} onSuccess={(fb) => { fetchTenders(); if (fb?.id) setFinancialBidDetailId(fb.id); }} />}
      {financialBidDetailId && <FinancialBidDetail financialBidId={financialBidDetailId} user={user} canManage={canManage} onClose={() => { setFinancialBidDetailId(null); fetchTenders(); }} />}
      {statusUpdateTender && (
        <div className="modal-overlay" onClick={closeStatusModal} role="presentation">
          <div ref={statusModalRef} role="dialog" aria-modal="true" aria-labelledby="master-status-modal-title" className="confirm-modal modal--sm" onClick={(e) => e.stopPropagation()}>
            <h3 id="master-status-modal-title">Update Tender Status</h3>
            <p>Update status for <strong>{statusUpdateTender.tender_ref}</strong></p>
            <div style={{ margin: '20px 0' }}>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db' }}>
                {options.statuses?.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeStatusModal} disabled={actionLoading}>Cancel</button>
              <button className="confirm-go-ahead-btn" onClick={handleStatusUpdate} disabled={actionLoading}>{actionLoading ? 'Updating...' : 'Update Status'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MasterTenderList;
